import { ChangeDetectionStrategy, Component, computed, forwardRef, inject, input, output, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { map, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { ApiParams, CoreApiService, queryAll } from '../../../core-api.service';
import { NotificationService } from '../../../notification.service';
import { ProjectsService } from '../../../projects/projects.service';
import { PermissionAction } from '../../../shared/enums/permission-action.enum';
import { XferStorage } from '../../../shared/models/xfer.model';
import { LOCAL_STORAGE } from './operation-storage-select.constants';

@Component({
  host: { 'test-id': 'operation-storage-select' },
  selector: 'operation-storage-select',
  styleUrls: ['./operation-storage-select.component.scss'],
  templateUrl: './operation-storage-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OperationStorageSelect),
      multi: true,
    },
  ],
  standalone: false,
})
export class OperationStorageSelect implements ControlValueAccessor {
  public protectedId = input<string>();
  public title = input<string>();
  public placeholder = input<string>();
  public optionsWidth = input<string>();
  public description = input<string>();
  public maxOptionsHeight = input<string>('350px');
  public isSelectDisabled = input<boolean>(false);
  public queryParams = input<string[]>([]);
  public permissionAction = input<PermissionAction>();

  public storageSelect = output<XferStorage>();

  private coreApi = inject(CoreApiService);
  private projectsService = inject(ProjectsService);
  private notifications = inject(NotificationService);

  private filter = signal('');
  protected currentValue = signal<string>('');
  protected isDisabled = signal(false);

  protected readonly extraOptions: XferStorage[] = [LOCAL_STORAGE];

  private hasPermission = rxResource({
    params: () => this.protectedId(),
    stream: () => this.projectsService.projectPermissions$.pipe(map((p) => p[this.protectedId()]?.includes(this.permissionAction()))),
  });

  protected storagesResource = rxResource({
    params: () => ({
      projectId: this.protectedId(),
      query: this.queryParams(),
      hasPermission: this.hasPermission.value(),
    }),
    stream: ({ params }) => {
      if (!params.hasPermission) {
        return of([]);
      }

      const queryParams: ApiParams = {
        sort: 'label',
        filter: [...params.query, `refs.project=${params.projectId}`, 'status_check=success'],
      };

      return queryAll(this.coreApi.xfer.storages.query, queryParams).pipe(
        tap((storages) => {
          if (!this.currentValue()) {
            return;
          }

          const allStorages = [...this.extraOptions, ...storages];
          const currentStorage = allStorages.find((storage) => storage._id === this.currentValue());
          this.onChange(currentStorage);
        }),
        catchError((e) => {
          if (e.status?.toString().startsWith('5')) {
            this.notifications.error('operationsStorageSelect.notifications.tryLater');
          } else {
            this.notifications.error('operationsStorageSelect.notifications.failedToGetStorages');
          }
          return of([]);
        }),
      );
    },
  });

  protected visibleOptions = computed(() => {
    const filter = this.filter().toLowerCase().trim();
    const items = this.storagesResource.value() ?? [];
    return new Set(items.filter((o) => o.label.toLowerCase().includes(filter)));
  });

  protected visibleExtraOptions = computed(() => {
    const filter = this.filter().toLowerCase().trim();
    return new Set(this.extraOptions.filter((o) => o.label.toLowerCase().includes(filter)));
  });

  private onChange: (value: XferStorage) => void;
  private onTouched: () => void;

  writeValue(value: XferStorage): void {
    this.currentValue.set(value?._id);
  }

  registerOnChange(fn: (value: XferStorage) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  protected filterChange(filter: string): void {
    this.filter.set(filter);
  }

  protected onStorageSelect(value: string): void {
    if (typeof value !== 'string') {
      return;
    }

    this.currentValue.set(value);

    const storages = this.storagesResource.value() ?? [];
    const extra = this.extraOptions.find((s) => s._id === value);

    if (extra) {
      const selectedStorage = {
        ...extra,
        url: extra.url,
        label: extra.url,
      };
      this.storageSelect.emit(selectedStorage);
      this.onChange?.(selectedStorage);
    } else {
      const selected = storages.find((s) => s._id === value);
      this.storageSelect.emit(selected);
      this.onChange?.(selected);
    }
    this.onTouched?.();
  }
}
